import { useContext, useState } from 'react';
import { useDisclosure, Button, useToast } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';

import { useAppSelector, useAppDispatch } from 'hooks';
import { ApplicationFormContext } from 'contexts/ApplicationFormContext';
import { apiAppealToUnderwriting } from 'api';
import { updateWipStatus as updateAutoWipStatus } from 'reducers/autoPersonalFormReducer';
import { updateWipStatus as updateHomeWipStatus } from 'reducers/homePersonalFormReducer';

import ReferToUnderwritingModal from './ReferToUnderwritingModal';

import { getMandatoryFileValidations } from './helpers';
import {
  WIP_STATUS_APPLICATION_CREATION,
  WIP_STATUS_APPEAL_SUBMITTED,
} from 'variables/constants';

interface ReferToUnderwritingProps {
  formStateKey: 'autoPersonalForm' | 'homePersonalForm';
}

const ReferToUnderwriting = ({ formStateKey }: ReferToUnderwritingProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const { uuid } = useParams<{ uuid: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const mandatoryFileValidations = getMandatoryFileValidations(formStateKey);
  const wipStatus = useAppSelector((state) => state[formStateKey].wip_status);

  const { validations } = useContext(ApplicationFormContext) as {
    validations: Validation[];
  };

  const remarks = useAppSelector((state) => state[formStateKey].remarks);
  const files = useAppSelector((state) => state[formStateKey].files);
  const appeal = useAppSelector((state) => state[formStateKey].appeal);
  const missingMandatoryFields = useAppSelector(
    (state) => state[formStateKey].missingMandatoryFields,
  );

  const hasMissingMandatoryFields = missingMandatoryFields.length > 0;

  // Track if we should keep component mounted while modal is handling submission
  const shouldShowButton =
    wipStatus === WIP_STATUS_APPLICATION_CREATION || isOpen;

  const handleSubmit = async (selectedItems: {
    validations: string[];
    remarks: string[];
    files: string[];
    comments: string;
    supporting_files?: number[];
  }) => {
    if (!uuid) {
      toast({
        title: 'Error',
        description: 'Policy UUID not found',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      throw new Error('Policy UUID not found');
    }

    setIsSubmitting(true);

    try {
      const response = await apiAppealToUnderwriting(uuid, selectedItems);
      return response;
    } catch (error: any) {
      console.error('Failed to submit appeal:', error);
      toast({
        title: 'Error',
        description: error?.message || 'Failed to submit appeal',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Callback to update wip_status when JIRA ticket is successfully created
  const handleSuccess = () => {
    const updateAction =
      formStateKey === 'autoPersonalForm'
        ? updateAutoWipStatus
        : updateHomeWipStatus;
    dispatch(updateAction(WIP_STATUS_APPEAL_SUBMITTED));
  };

  // Hide the button once appeal is submitted, but keep modal mounted if open
  if (!shouldShowButton) {
    return null;
  }

  return (
    <>
      {wipStatus === WIP_STATUS_APPLICATION_CREATION && (
        <Button
          colorScheme="brand"
          width="100%"
          onClick={onOpen}
          mb="20px"
          isDisabled={hasMissingMandatoryFields}
        >
          Refer to Underwriting
        </Button>
      )}

      <ReferToUnderwritingModal
        isOpen={isOpen}
        onClose={onClose}
        validations={validations}
        remarks={remarks}
        files={files}
        uuid={uuid || ''}
        onSubmit={handleSubmit}
        onSuccess={handleSuccess}
        isSubmitting={isSubmitting}
        appeal={appeal}
        mandatoryFileValidations={mandatoryFileValidations}
      />
    </>
  );
};

export default ReferToUnderwriting;
